import type { ReactElement } from "react";
import { ErrorState } from "./state-components/error-state";
import { NotFoundState } from "./state-components/not-found-state";
import { usePageLayoutState } from "./hooks/use-page-layout-state";
import { PageHeader } from "./shared-components/page-header";
import { PageWrapper } from "./shared-components/page-wrapper";
import type { DetailPageLayoutProps } from "./types";

/**
 * DetailPageLayout - Reusable layout for detail/edit pages
 *
 * Handles loading, error, not-found, and success states automatically.
 * Supports dynamic titles that can be computed from the data.
 * Always includes back navigation (required for detail pages).
 */
export function DetailPageLayout<T>({
  fetching,
  data,
  title,
  backLink,
  backLabel,
  subtitle: subtitleProp,
  children,
  loadingComponent,
  errorStateProps,
  notFoundStateProps,
  className,
  avatar,
}: DetailPageLayoutProps<T>): ReactElement {
  const hasData = data !== null && data !== undefined;
  const { isLoading, isError, isSuccess } = usePageLayoutState({
    fetching,
    alwaysIdleAsLoading: true,
    keepDataWhileRefetching: true,
  });

  // Resolve dynamic title - use static title during loading/error/not-found
  const resolvedTitle = typeof title === "function" ? (hasData ? title(data) : backLabel) : title;
  const fallbackLabel = typeof title === "string" ? title : backLabel;

  // Resolve dynamic subtitle
  const resolvedSubtitle =
    subtitleProp === undefined ? backLabel : typeof subtitleProp === "function" ? (hasData ? subtitleProp(data) : backLabel) : subtitleProp;

  // Resolve dynamic avatar
  const resolvedAvatar = typeof avatar === "function" ? (hasData ? avatar(data) : undefined) : avatar;

  const renderHeader = (headerTitle: string, headerSubtitle: string) => (
    <PageHeader title={headerTitle} subtitle={headerSubtitle} backLink={backLink} backLabel={backLabel} avatar={resolvedAvatar} />
  );

  // Render loading state
  if (isLoading) {
    return (
      <PageWrapper className={className}>
        {renderHeader(fallbackLabel, backLabel)}
        {loadingComponent}
      </PageWrapper>
    );
  }

  // Render error state
  if (isError) {
    return (
      <PageWrapper className={className}>
        {renderHeader(fallbackLabel, backLabel)}
        <ErrorState {...errorStateProps} />
      </PageWrapper>
    );
  }

  // Render not found state (no data after success)
  if (isSuccess && !hasData) {
    return (
      <PageWrapper className={className}>
        {renderHeader(fallbackLabel, backLabel)}
        <NotFoundState {...notFoundStateProps} />
      </PageWrapper>
    );
  }

  // Render success state with data
  if (hasData) {
    return (
      <PageWrapper className={className}>
        {renderHeader(resolvedTitle, resolvedSubtitle)}
        {children(data)}
      </PageWrapper>
    );
  }

  // Fallback
  return (
    <PageWrapper className={className}>
      {renderHeader(fallbackLabel, backLabel)}
      {loadingComponent}
    </PageWrapper>
  );
}
